import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
} from 'react';
import format from 'string-template';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { compose } from 'recompose';

import useComponentMounted from '../../../../../hooks/useComponentMounted';
import LoggedInUserContext from '../../../../../context/LoggedInUserContext';
import useToast from '../../../../hooks/useToast';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import InputLabel from '../../../../../components/v2/InputLabel';
import { Card, Container } from '../../../../../components/v2/Card';
import { SaveButton } from '../../../../../components/Button/ActionButtons';
import { StyledTextField, StyledTextFieldContainer } from '../../../../../components/Inputs';
import ConfirmDialog from '../../../../components/ConfirmDialog';

import texts from '../texts.json';

const AppIconNameSettings = ({ coachName, coachAppCustomization }) => {
  const { userDoc } = useContext(LoggedInUserContext);
  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();

  const [appIconName, setAppIconName] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setAppIconName(coachAppCustomization?.appIconName ?? '');
  }, [coachAppCustomization]);

  const handleSaveClick = useCallback(() => {
    if (!appIconName) {
      setShowError(true);
      return;
    }
    setShowConfirm(true);
  }, [appIconName]);

  const handleSave = useCallback(async () => {
    setShowConfirm(false);
    setIsLoading(true);
    try {
      await coachAppCustomization.updateFields({
        appIconName,
        lastUpdatedByUserName: userDoc.name,
      });
      showToast(texts.saveSuccessful);
    } catch (error) {
      showToast(format(texts.saveFailed, { error }), { error: true });
    }
    if (isComponentMountedRef.current) {
      setIsLoading(false);
    }
  }, [
    coachAppCustomization,
    appIconName,
    userDoc,
    showToast,
    isComponentMountedRef,
  ]);

  return (
    <>
      <Card>
        <Container>
          <StyledTextFieldContainer $withMargin>
            <InputLabel>{texts.appIconName}</InputLabel>
            <StyledTextField
              value={appIconName}
              onChange={(e) => {
                setAppIconName(e.target.value);
                setShowError(false);
              }}
              error={showError && !appIconName}
              helperText={showError && !appIconName ? texts.appIconNameRequired : ''}
            />
          </StyledTextFieldContainer>
          <SaveButton onClick={handleSaveClick} disabled={isLoading} $fitToContent>
            {texts.save}
          </SaveButton>
        </Container>
      </Card>
      <ConfirmDialog
        isOpen={showConfirm}
        onConfirm={handleSave}
        onCancel={() => setShowConfirm(false)}
        dialogTexts={{
          title: format(texts.appIconNameConfirmTitle, { coach: coachName }),
          content: format(texts.appIconNameChange, { appIconName }),
        }}
      />
      <LoadingOverlay isLoading={isLoading} />
    </>
  );
};

AppIconNameSettings.propTypes = {
  coachName: PropTypes.string.isRequired,
  coachAppCustomization: PropTypes.object,
};

AppIconNameSettings.defaultProps = {
  coachAppCustomization: null,
};

export default compose(observer)(AppIconNameSettings);
